import {
  Alert,
  Divider,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
} from "@mui/material";
import FormTextField from "../FormInputElements/FormTextField";
import FormSelectField from "../FormInputElements/FormSelectField";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useEffect } from "react";
import dayjs from "dayjs";
import CropYearDatePicker from "./CropYearDatePicker";
import { useReferenceData } from "../../hooks/useReferenceData";
import { useSearchParams } from "react-router-dom";
import { WATER_LABELS, WATER_HELP, ENERGY_HELP, ENERGY_SOURCE_UNITS, ENERGY_UNITS, cropIrrigation, energySourceChange, irrigationErrors } from "../../utils/irrigation";
import { cropYieldError, isSummerFallow, requiresMetricYield } from "../../utils/cropYield";
import {
  HOLOS_UNSUPPORTED_CROP_MESSAGE,
  isHolosCropSupported,
} from "../../constants/holos";

const RESIDUE_PRACTICES = [
  "Retained on surface",
  "Incorporated",
  "Removed",
  "Grazed",
  "Burned",
];

export default function RotationForm({
  LowerPanel,
  panelControls,
  cropYear,
  setCropYear,
}) {
  const [searchParams] = useSearchParams();
  const { referenceData } = useReferenceData([
    "crops",
    "crop_frequency",
    "yield_units",
    "tillage_regimes",
  ]);
  const issueField = searchParams.get("issueField");
  const irrigation = cropIrrigation(cropYear);
  const waterErrors = cropYear.is_irrigated ? irrigationErrors(irrigation) : {};
  const fallow = isSummerFallow(cropYear.crop_this_year);
  const yieldError = cropYieldError(cropYear.crop_this_year, cropYear.yield_value);
  const yieldIsInvalid = Boolean(yieldError);
  const metricYield = requiresMetricYield(cropYear.crop_this_year);
  const organicMatterValue = cropYear.soil_organic_matter_percent;
  const organicMatterIsInvalid =
    organicMatterValue !== "" &&
    organicMatterValue !== null &&
    organicMatterValue !== undefined &&
    (!Number.isFinite(Number(organicMatterValue)) ||
      Number(organicMatterValue) < 0 ||
      Number(organicMatterValue) > 100);

  const hasIssue = (fieldName) => issueField === fieldName;

  function handleStateChange(target, key) {
    setCropYear((prevState) => ({ ...prevState, [key]: target }));
  }

  useEffect(() => {
    const element = document.getElementById("scrollableDiv");
    element.scrollTo({ top: 0, left: 0, behavior: "smooth" });

    const target = document.getElementById("cropyear-rotation-form");
    if (target && issueField) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [issueField]);

  return (
    <div id="cropyear-rotation-form" className="h-full w-full">
      <h3 className="text-[24px] font-semibold text-slate-800">
        Rotation and Residue Practices
      </h3>
      <Divider sx={{ marginBottom: 2, marginTop: 1 }} />
      <p className="mb-4 max-w-[760px] text-sm leading-6 text-slate-600">
        Every field on this page is optional while the crop year is in draft.
        You can save now and come back later. We&apos;ll show anything still
        needed before analysis on the review screen.
      </p>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={
            Number.isInteger(cropYear.crop_year)
              ? dayjs(cropYear.crop_year.toString())
              : null
          }
          disableFuture
          views={["year"]}
          onChange={(e) => {
            if (e !== null) handleStateChange(e.$y, "crop_year");
          }}
          sx={{ marginBottom: 2 }}
          slotProps={{
            textField: {
              error: hasIssue("crop_year"),
              size: "small",
            },
          }}
          label="Crop Year"
        />
      </LocalizationProvider>

      <FormSelectField
        errorFound={hasIssue("crop_this_year")}
        valuesArray={referenceData.crops || []}
        fieldLabel={"Crop This year"}
        fieldState={cropYear.crop_this_year}
        onChange={(e) => setCropYear((current) => ({
          ...current,
          crop_this_year: e.target.value,
          yield_units: requiresMetricYield(e.target.value) && current.yield_units === "bu/ac"
            ? "" : current.yield_units,
        }))}
        helperText={""}
        modalTitle={"Crop This Year"}
        modalDescription={"The crop for this year."}
      />

      {cropYear.crop_this_year &&
      !isHolosCropSupported(cropYear.crop_this_year, cropYear.holos_supported_crops) ? (
        <Alert severity="warning" className="mb-4 max-w-[760px]">
          {HOLOS_UNSUPPORTED_CROP_MESSAGE}
        </Alert>
      ) : null}

      <FormSelectField
        errorFound={hasIssue("crop_frequency")}
        valuesArray={referenceData.crop_frequency || []}
        fieldLabel={"Crop Frequency"}
        onChange={(e) => handleStateChange(e.target.value, "crop_frequency")}
        helperText={"Optional record; not used by the current Holos calculation"}
        modalTitle={"Crop Frequency"}
        modalDescription={
          "Please select the frequency of this crop in your rotation, if none apply, select the closest option."
        }
        fieldState={cropYear.crop_frequency}
      />
      <div className="flex flex-wrap items-start gap-4">
        <div className="min-w-[220px]">
          <FormTextField
            errorFound={hasIssue("yield_value") || yieldIsInvalid}
            isNumber={true}
            fieldState={cropYear.yield_value}
            fieldLabel={"Yield"}
            helperText={
              fallow
                ? "Summer fallow has no harvest. Enter zero."
                : yieldError
            }
            inputProps={{ min: 0, step: "any" }}
            modalOff={true}
            onChange={(t) => handleStateChange(t, "yield_value")}
          />
        </div>
        <div className="w-40">
          <FormSelectField
            errorFound={hasIssue("yield_units")}
            valuesArray={(referenceData.yield_units || []).filter((unit) => !metricYield || unit.value !== "bu/ac")}
            fieldLabel={"Units"}
            onChange={(e) => handleStateChange(e.target.value, "yield_units")}
            helperText={metricYield ? "Use mass units for this crop." : ""}
            modalTitle={"Yield value and Units"}
            modalDescription={
              "Please select the yield of your crop, our units are bushels per acre, tonnes (metric) per acre and tonnes (metric) per hectare"
            }
            fieldState={cropYear.yield_units}
          />
        </div>
      </div>
      <FormSelectField
        errorFound={hasIssue("previous_crop")}
        valuesArray={referenceData.crops || []}
        fieldLabel={"Crop Prior year"}
        onChange={(e) => handleStateChange(e.target.value, "previous_crop")}
        helperText={"Optional record; does not create a Holos rotation"}
        modalTitle={"Crop Prior Year"}
        modalDescription={"The crop for the previous year."}
        fieldState={cropYear.previous_crop}
      />

      <Divider sx={{ marginBottom: 2, marginTop: 2 }} />
      <h4 className="mb-3 text-[20px] font-semibold text-slate-700">
        Tillage and residue
      </h4>
      <div className="grid max-w-[900px] grid-cols-1 gap-x-4 md:grid-cols-2">
        <FormSelectField
          valuesArray={referenceData.tillage_regimes || []}
          fieldLabel="Tillage Practice"
          fieldState={cropYear.tillage_regime}
          onChange={(e) => handleStateChange(e.target.value, "tillage_regime")}
          helperText="Practice used for this crop year"
          modalTitle="Tillage Practice"
          modalDescription="The tillage practice used for this crop year."
        />
        <FormSelectField
          valuesArray={referenceData.tillage_regimes || []}
          fieldLabel="Previous Tillage Practice"
          fieldState={cropYear.previous_tillage_regime}
          onChange={(e) =>
            handleStateChange(e.target.value, "previous_tillage_regime")
          }
          helperText="Optional historical context"
          modalTitle="Previous Tillage Practice"
          modalDescription="The tillage practice used before the current practice."
        />
        <CropYearDatePicker
          cropYear={cropYear.crop_year}
          label="Practice Change Date"
          value={(cropYear.regime_change_date || "").slice(0, 10)}
          onChange={(value) => handleStateChange(value, "regime_change_date")}
        />
        <TextField
          select
          size="small"
          label="Residue Management"
          value={cropYear.residue_management || ""}
          onChange={(e) => handleStateChange(e.target.value, "residue_management")}
        >
          {RESIDUE_PRACTICES.map((practice) => (
            <MenuItem key={practice} value={practice}>
              {practice}
            </MenuItem>
          ))}
        </TextField>
      </div>

      <Divider sx={{ marginBottom: 2, marginTop: 3 }} />
      <h4 className="mb-3 text-[20px] font-semibold text-slate-700">
        Soil organic matter
      </h4>
      <div className="flex max-w-[620px] flex-wrap gap-4">
        <TextField
          error={organicMatterIsInvalid}
          type="number"
          size="small"
          label="Organic Matter (%)"
          value={cropYear.soil_organic_matter_percent ?? ""}
          onChange={(e) =>
            handleStateChange(e.target.value, "soil_organic_matter_percent")
          }
          inputProps={{ min: 0, max: 100, step: "any" }}
          helperText={organicMatterIsInvalid ? "Use 0 to 100%." : ""}
        />
        <CropYearDatePicker
          cropYear={cropYear.crop_year}
          label="Sample Date"
          value={(cropYear.soil_organic_matter_date || "").slice(0, 10)}
          onChange={(value) =>
            handleStateChange(value, "soil_organic_matter_date")
          }
        />
      </div>

      <Divider sx={{ marginBottom: 2, marginTop: 3 }} />
      <div className="flex items-center justify-between max-w-[900px]">
        <h4 className="text-[20px] font-semibold text-slate-700">Irrigation</h4>
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(cropYear.is_irrigated)}
              onChange={(e) => handleStateChange(e.target.checked, "is_irrigated")}
            />
          }
          label="Irrigated"
        />
      </div>
      {cropYear.is_irrigated ? (
        <div className="mt-3 grid max-w-[900px] grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <p className="text-sm text-slate-600 sm:col-span-2 lg:col-span-3">{WATER_HELP}</p>
          <TextField
            type="number"
            size="small"
            label="Water Applied"
            error={Boolean(waterErrors.waterApplied)}
            helperText={waterErrors.waterApplied || "Total for this crop year"}
            value={cropYear.irrigation_water_applied ?? ""}
            onChange={(e) =>
              handleStateChange(e.target.value, "irrigation_water_applied")
            }
            inputProps={{ min: 0, step: "any" }}
          />
          <TextField
            select
            size="small"
            label="Water Unit"
            error={Boolean(waterErrors.waterUnit)}
            helperText={waterErrors.waterUnit}
            value={cropYear.irrigation_water_unit || ""}
            onChange={(e) =>
              handleStateChange(e.target.value, "irrigation_water_unit")
            }
          >
            {irrigation.waterUnit && !WATER_LABELS[irrigation.waterUnit] && <MenuItem value={irrigation.waterUnit} disabled>{irrigation.waterUnit} (choose a supported unit)</MenuItem>}
            {Object.entries(WATER_LABELS).filter(([unit]) => !["inch", "inches"].includes(unit) || irrigation.waterUnit === unit).map(([unit, label]) => <MenuItem key={unit} value={unit}>{label}</MenuItem>)}
          </TextField>
          <TextField
            select
            size="small"
            label="Energy Source"
            error={Boolean(waterErrors.energySource)}
            helperText={waterErrors.energySource}
            value={cropYear.irrigation_energy_source || ""}
            onChange={(e) => setCropYear(current => {
              const next = energySourceChange(cropIrrigation(current), e.target.value);
              return { ...current, irrigation_energy_source: next.energySource, irrigation_energy_unit: next.energyUnit };
            })}
          >
            <MenuItem value="">Not recorded</MenuItem>
            {irrigation.energySource && !ENERGY_SOURCE_UNITS[irrigation.energySource] && <MenuItem value={irrigation.energySource} disabled>{irrigation.energySource} (choose a supported source)</MenuItem>}
            {Object.keys(ENERGY_SOURCE_UNITS).map(
              (source) => (
                <MenuItem key={source} value={source}>{source}</MenuItem>
              )
            )}
          </TextField>
          <TextField
            type="number"
            size="small"
            label="Energy Used"
            error={Boolean(waterErrors.energyUsed)}
            helperText={waterErrors.energyUsed || "Total for this field"}
            value={cropYear.irrigation_energy_used ?? ""}
            onChange={(e) =>
              handleStateChange(e.target.value, "irrigation_energy_used")
            }
            inputProps={{ min: 0, step: "any" }}
          />
          <TextField
            select
            size="small"
            label="Energy Unit"
            error={Boolean(waterErrors.energyUnit)}
            helperText={waterErrors.energyUnit || "Choose a unit matching the energy source"}
            value={cropYear.irrigation_energy_unit || ""}
            onChange={(e) =>
              handleStateChange(e.target.value, "irrigation_energy_unit")
            }
          >
            <MenuItem value="">Not recorded</MenuItem>
            {Array.from(new Set([...(ENERGY_SOURCE_UNITS[irrigation.energySource] || ENERGY_UNITS), ...(irrigation.energyUnit ? [irrigation.energyUnit] : [])])).map((unit) => (
              <MenuItem key={unit} value={unit} disabled={Boolean(irrigation.energySource && !ENERGY_SOURCE_UNITS[irrigation.energySource]?.includes(unit))}>{unit === "m3" ? "m³" : unit}</MenuItem>
            ))}
          </TextField>
          <p className="text-sm text-slate-600 sm:col-span-2 lg:col-span-3">{ENERGY_HELP}</p>
        </div>
      ) : null}

      <LowerPanel
        activeStep={panelControls.activeStep}
        handleBack={panelControls.handleBack}
        handleNext={panelControls.handleNext}
        steps={panelControls.steps}
        handleComplete={panelControls.handleComplete}
        completedSteps={panelControls.completedSteps}
        totalSteps={panelControls.totalSteps}
        isInputValid={() => !yieldIsInvalid && !organicMatterIsInvalid && !Object.keys(waterErrors).length}
      />
    </div>
  );
}
